import { useState } from "react";
import { Paperclip, FileUp, Camera, Film, FilePlus2, AudioLines, Mic, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DailyHint } from "@/components/MessageTools";

const GROUPS = [
  { label: "Add", items: [
    { id: "file", icon: FileUp, name: "Upload a file", desc: "PDF, image, text, Word or Excel" },
    { id: "camera", icon: Camera, name: "Take a photo", desc: "Snap homework or notes" },
    { id: "video", icon: Film, name: "Add a video", desc: "Upload a lecture or paste a link" },
  ] },
  { label: "Create", items: [{ id: "document", icon: FilePlus2, name: "Make a document", desc: "PDF, Word, slides, spreadsheets and more" }] },
  { label: "Talk", items: [
    { id: "voice", icon: AudioLines, name: "Voice mode", desc: "Talk with your tutor hands-free", daily: true },
    { id: "mic", icon: Mic, name: "Speak your question", desc: "Dictate instead of typing", daily: true },
  ] },
];

export function ToolsMenu({ onPick, disabled, uploading }) {
  const [open, setOpen] = useState(false);
  const pick = (id) => { setOpen(false); onPick(id); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button disabled={disabled} data-testid="tools-menu-btn" title="Attach, create or talk" aria-label="Attach files and more tools"
          className={`h-9 w-9 rounded-xl flex items-center justify-center transition-colors disabled:opacity-40 ${open ? "bg-sky-400 text-white" : "text-neutral-300 hover:text-white hover:bg-white/10"}`}>
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" align="start" sideOffset={10} className="w-[min(20rem,calc(100vw-1.5rem))] p-2 bg-[#121725] border-white/10 rounded-2xl text-white max-h-[70vh] overflow-y-auto" data-testid="tools-menu">
        {GROUPS.map((g) => (
          <div key={g.label} className="py-1">
            <p className="px-2 pb-1 text-[10px] uppercase tracking-[0.16em] text-neutral-500">{g.label}</p>
            {g.items.map((it) => (
              <button key={it.id} onClick={() => pick(it.id)} data-testid={`tools-${it.id}`}
                className="w-full flex items-center gap-3 px-2 py-2.5 rounded-xl text-left hover:bg-white/[0.06] active:bg-white/[0.08] transition-colors">
                <span className="h-8 w-8 rounded-lg bg-sky-400/10 text-sky-300 flex items-center justify-center shrink-0"><it.icon className="h-4 w-4" /></span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm text-neutral-100">{it.name}</span>
                  <span className="block text-[11px] text-neutral-500 leading-snug">{it.desc}</span>
                </span>
                {it.daily && <DailyHint kind="voice" testid={`tools-${it.id}-daily`} />}
              </button>
            ))}
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}
